import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, call } from '@/api/client';
import { qk } from '@/api/keys';
import { messageOf } from '@/api/errors';
import { useInstruments, useWeightPresets } from '@/api/hooks/core';
import { useTemplates } from '@/api/hooks/lists';
import type { InstrumentOut, OnboardingSetupOut, SharedStrategyOut, TemplateOut } from '@/api/schema';
import { Banner, Button, ConfirmDialog, DirectionPill, EmptyState, ErrorState, Icon, IconButton, LoadingArea, Pill, SearchField, Segmented } from '@/components/ui';
import { ConditionList, type ConditionRowData } from '@/components/domain/Domain';
import { DISCLAIMERS } from '@/lib/copy';
import { forgetShare } from '@/lib/pendingShare';
import { assetGroupLabel, planLabel } from '@/lib/domain';
import { searchInstruments } from '@/lib/instrumentSearch';
import { cx } from '@/lib/cx';
import { useResolvedTheme } from '@/theme/theme';
import logoDark from '@/assets/text_logo_dark.png';
import logoLight from '@/assets/text_logo_light.png';
import s from './Onboarding.module.css';

/**
 * The guided first-run — the phone's `onboarding_screen.dart` (S28–S31) laid out for the web: four
 * steps that end with a real strategy and a real watchlist. Intro, choose a template, choose the
 * instruments, then what was created. Progress is the row of four bars; Skip sits top-right on the
 * first three steps and confirms first; the done step has no Skip and no Back, because by then the
 * objects exist. Copy is the phone's `OnboardingText`, verbatim — change both together.
 *
 * Shown by the root layout after sign-in and the legal gate, only while the profile says setup is
 * owed and this browser has not finished or skipped it (`seen.ts`).
 *
 * **From a share link the shared strategy stands in for the template** when the plan covers it:
 * three steps, the intro naming the strategy, and the done step opening it. A share the plan does
 * not cover keeps the template flow, with the intro saying the share will be saved, paused — the
 * share page saves it once the flow closes.
 */
type Step = 'intro' | 'template' | 'instruments' | 'done';
const STEPS: Step[] = ['intro', 'template', 'instruments', 'done'];
const SHARE_STEPS: Step[] = ['intro', 'instruments', 'done'];

const WATCHLIST_NAME = 'My watchlist';

/**
 * The instrument step's default selection, when the catalogue offers them: names a newcomer knows.
 * The catalogue is alphabetical, so "the first three" was AAVE, ADA and ALGO. Whatever the country
 * or plan leaves out is skipped, and the first available instruments fill any gap.
 */
const DEFAULT_PICKS = ['BTC/USD', 'ETH/USD', 'EUR/USD', 'XAU/USD', 'SOL/USD', 'GBP/USD'];

function defaultPicks(usable: InstrumentOut[]): string[] {
  const bySymbol = new Map(usable.map((i) => [i.symbol, i.id]));
  const known = DEFAULT_PICKS.map((sym) => bySymbol.get(sym)).filter((id): id is string => !!id);
  const rest = usable.map((i) => i.id).filter((id) => !known.includes(id));
  return [...known, ...rest].slice(0, 3);
}

export function OnboardingFlow({
  cryptoOnly,
  share = null,
  doneLabel,
  onLeave,
}: {
  cryptoOnly: boolean;
  /** The share the user arrived on, when they did. */
  share?: SharedStrategyOut | null;
  /** "Go to Today", or "Continue" when the user arrived on a link the flow should hand back to. */
  doneLabel: string;
  /** Finished or skipped. `toAccount`: the done step's invite-code door; `strategyId`: open it. */
  onLeave: (next?: { toAccount?: boolean; strategyId?: string }) => void;
}) {
  const qc = useQueryClient();
  const fromShare = share !== null && !share.locked_reason && share.rules.some((r) => !r.unavailable);
  const steps = fromShare ? SHARE_STEPS : STEPS;
  const [step, setStep] = useState<Step>('intro');
  const [template, setTemplate] = useState<TemplateOut | null>(null);
  const [instrumentIds, setInstrumentIds] = useState<string[]>([]);
  // Whether the default selection has been offered: preselecting whenever the set is empty would
  // re-fill it the moment the user cleared it, and they could never reach zero.
  const [preselected, setPreselected] = useState(false);
  const [confirmSkip, setConfirmSkip] = useState(false);

  const setup = useMutation({
    mutationFn: () =>
      call(
        api.POST('/api/v1/onboarding/setup', {
          body: fromShare
            ? { share_code: share!.code, instrument_ids: instrumentIds, watchlist_name: WATCHLIST_NAME }
            : { template_id: template!.id, instrument_ids: instrumentIds, watchlist_name: WATCHLIST_NAME },
        }),
      ),
    onSuccess: () => {
      // The share is now the account's first strategy: nothing is left to save afterwards.
      if (fromShare) forgetShare();
      // The account now has a strategy and a list: the profile's `setup_needed`, Strategies,
      // Watchlists and Today all read differently from here on.
      for (const key of [qk.me, qk.strategies.all, qk.watchlists.all, qk.ranking, qk.applications]) {
        void qc.invalidateQueries({ queryKey: key });
      }
      setStep('done');
    },
  });

  const resetSetup = setup.reset;
  const chooseTemplate = useCallback(
    (t: TemplateOut) => {
      setTemplate(t);
      resetSetup();
    },
    [resetSetup],
  );
  const chooseInstruments = useCallback(
    (ids: string[]) => {
      setInstrumentIds(ids);
      resetSetup();
    },
    [resetSetup],
  );
  const preselect = useCallback(
    (ids: string[]) => {
      if (preselected) return;
      setPreselected(true);
      setInstrumentIds(ids);
    },
    [preselected],
  );

  const index = steps.indexOf(step);
  const back = step === 'template' || step === 'instruments' ? () => setStep(steps[index - 1]!) : undefined;
  const strategyName = fromShare ? share!.name : (template?.name ?? '');

  return (
    <div className={s.screen}>
      <div className={s.card}>
        <div className={s.chrome}>
          <span className={s.chromeSide}>{back && <IconButton icon="arrow_back" label="Back" size="sm" onClick={back} />}</span>
          <ProgressBars count={steps.length} filled={index + 1} />
          <span className={cx(s.chromeSide, s.chromeEnd)}>
            {step !== 'done' && (
              // Muted rather than the brand colour: Skip is the way past the setup, and colouring it
              // like the primary action puts the two on the same footing.
              <button type="button" className={s.skip} onClick={() => setConfirmSkip(true)}>
                Skip
              </button>
            )}
          </span>
        </div>

        {step === 'intro' &&
          (fromShare ? (
            <ShareIntroStep share={share!} onContinue={() => setStep('instruments')} />
          ) : (
            <IntroStep cryptoOnly={cryptoOnly} lockedShare={share} onContinue={() => setStep('template')} />
          ))}
        {step === 'template' && (
          <TemplateStep
            chosen={template}
            onChoose={chooseTemplate}
            onContinue={() => setStep('instruments')}
          />
        )}
        {step === 'instruments' && (
          <InstrumentsStep
            strategyName={strategyName}
            selected={instrumentIds}
            onChange={chooseInstruments}
            preselect={preselect}
            error={setup.error}
            creating={setup.isPending}
            onCreate={() => setup.mutate()}
          />
        )}
        {step === 'done' && setup.data && (
          <DoneStep
            result={setup.data}
            conditions={fromShare ? shareConditions(share!) : template?.rules}
            instrumentIds={instrumentIds}
            doneLabel={fromShare && setup.data.strategy ? `Open ${setup.data.strategy.name}` : doneLabel}
            onDone={() => onLeave(fromShare && setup.data.strategy ? { strategyId: setup.data.strategy_id } : undefined)}
            onInviteCode={() => onLeave({ toAccount: true })}
          />
        )}
      </div>

      <ConfirmDialog
        open={confirmSkip}
        onClose={() => setConfirmSkip(false)}
        onConfirm={() => {
          setConfirmSkip(false);
          onLeave();
        }}
        title="Skip setup?"
        confirmLabel="Skip"
      >
        {/* Once a template is chosen the dialog names the loss: a generic "skip setup?" understates
            what Skip throws away after two choices. */}
        {fromShare
          ? `${share!.name} will still be saved to your Strategies, but nothing will watch the markets for it until you add it to a watchlist.`
          : template && step === 'instruments'
            ? `Are you sure? Your selected strategy, ${template.name}, won't be created.`
            : 'You can build a strategy and watchlist yourself from Strategies and Watchlists.'}
      </ConfirmDialog>
    </div>
  );
}

/** Bars rather than dots: a bar that fills reads as progress where equal dots read as decoration. */
function ProgressBars({ count, filled }: { count: number; filled: number }) {
  return (
    <div className={s.bars} role="progressbar" aria-valuemin={1} aria-valuemax={count} aria-valuenow={filled} aria-label={`Step ${filled} of ${count}`}>
      {Array.from({ length: count }, (_, i) => (
        <span key={i} className={cx(s.bar, i < filled && s.barOn)} />
      ))}
    </div>
  );
}

/** A step's frame: heading, the step's content, then the action and its footer line. */
function StepFrame({
  title,
  body,
  children,
  action,
  footer = DISCLAIMERS.monitoring,
  centred,
  logo,
}: {
  title: string;
  body: string;
  children: ReactNode;
  action: ReactNode;
  footer?: string;
  centred?: boolean;
  logo?: boolean;
}) {
  const theme = useResolvedTheme();
  return (
    <>
      <div className={cx(s.content, centred && s.centred)}>
        {logo && <img src={theme === 'light' ? logoLight : logoDark} alt="SixOne8" className={s.logo} />}
        <h1 className={s.title}>{title}</h1>
        <p className={s.body}>{body}</p>
        {children}
      </div>
      <div className={s.actions}>
        {action}
        <p className={s.footer}>{footer}</p>
      </div>
    </>
  );
}

// --- S28: intro -----------------------------------------------------------------------------------

function IntroStep({
  cryptoOnly,
  lockedShare,
  onContinue,
}: {
  cryptoOnly: boolean;
  /** A share the plan does not cover: said up front, so the template step is not a surprise. */
  lockedShare: SharedStrategyOut | null;
  onContinue: () => void;
}) {
  return (
    <StepFrame
      title="Let's set up your first strategy"
      body="Two quick choices and SixOne8 starts watching the market for you. Takes under a minute."
      footer="SixOne8 is a monitoring tool, not financial advice. Trading carries risk of loss."
      logo
      action={
        <Button variant="primary" size="lg" block onClick={onContinue}>
          Get started
        </Button>
      }
    >
      <ol className={s.steps}>
        <NumberedStep number={1} title="Choose a strategy template">
          A set of conditions that describe a market moment worth knowing about.
        </NumberedStep>
        <NumberedStep number={2} title="Pick instruments to watch">
          {/* A crypto-only account (country_asset_gating.md) is never introduced to an asset class
              it can never hold. */}
          {cryptoOnly
            ? 'Bitcoin, Ethereum, Solana and more. These become your first watchlist.'
            : 'Forex pairs and crypto. These become your first watchlist.'}
        </NumberedStep>
      </ol>
      {lockedShare ? (
        <Banner tone="brand">
          <strong>{lockedShare.name}</strong> needs{' '}
          {lockedShare.required_plan ? `the ${planLabel(lockedShare.required_plan)} plan` : 'a higher plan'}, so it will be saved to your
          Strategies, paused, when you finish. First, set up a strategy your plan runs.
        </Banner>
      ) : (
        <Banner tone="brand">Nothing here is permanent. You can edit or delete both at any time.</Banner>
      )}
    </StepFrame>
  );
}

/** A share's conditions as a condition list draws them; unavailable ones are not copied. */
function shareConditions(share: SharedStrategyOut) {
  return share.rules.filter((r) => !r.unavailable).map((r, i) => ({ ...r, rule_id: String(i) }));
}

/** The intro when the strategy comes from the share: what they were sent, then one choice left. */
function ShareIntroStep({ share, onContinue }: { share: SharedStrategyOut; onContinue: () => void }) {
  const presets = useWeightPresets().data;
  return (
    <StepFrame
      title={`${share.name} is ready`}
      body="Pick the markets to watch and SixOne8 starts checking them for it. Takes under a minute."
      footer="A shared strategy is someone else's rules, not a recommendation. SixOne8 is a monitoring tool, not financial advice."
      logo
      action={
        <Button variant="primary" size="lg" block onClick={onContinue}>
          Pick markets to watch
        </Button>
      }
    >
      <div className={s.doneCard}>
        <span className={s.groupLabel}>{share.owner_display_name ? `Shared by ${share.owner_display_name}` : 'Shared with you'}</span>
        <span className={s.choiceHead}>
          <span className={s.choiceTitle}>{share.name}</span>
          <DirectionPill direction={share.direction} size="xs" />
        </span>
        <ConditionList conditions={shareConditions(share)} presets={presets} showState={false} dense />
      </div>
      {(share.unavailable_count ?? 0) > 0 && (
        <Banner tone="warn">
          {share.unavailable_count} condition{share.unavailable_count === 1 ? ' uses' : 's use'} an indicator that is no longer available and
          will be left out.
        </Banner>
      )}
      <Banner tone="brand">It becomes your own copy. Edit or delete it at any time.</Banner>
    </StepFrame>
  );
}

function NumberedStep({ number, title, children }: { number: number; title: string; children: ReactNode }) {
  return (
    <li className={s.step}>
      <span className={s.stepNumber}>{number}</span>
      <span>
        <span className={s.stepTitle}>{title}</span>
        <span className={s.stepBody}>{children}</span>
      </span>
    </li>
  );
}

// --- S29: choose a template -----------------------------------------------------------------------

/** "2 conditions · 1h", or every timeframe the rules span — one template runs on three. */
function conditionsCaption(rules: TemplateOut['rules']): string {
  const timeframes = [...new Set(rules.map((r) => r.timeframe || '—'))];
  return `${rules.length} ${rules.length === 1 ? 'condition' : 'conditions'} · ${timeframes.join('/')}`;
}

function TemplateStep({
  chosen,
  onChoose,
  onContinue,
}: {
  chosen: TemplateOut | null;
  onChoose: (t: TemplateOut) => void;
  onContinue: () => void;
}) {
  const templates = useTemplates();
  const presets = useWeightPresets().data;
  // **Locked templates are not shown.** Everywhere else a lock is an upsell; here it is friction in
  // the one flow that must have none. What the plan allows is the list.
  const usable = useMemo(() => (templates.data ?? []).filter((t) => !t.locked_reason), [templates.data]);
  // The first is the starter-marked template, which browse order puts first.
  const first = usable[0];
  useEffect(() => {
    if (!chosen && first) onChoose(first);
  }, [chosen, first, onChoose]);

  return (
    <StepFrame
      title="Choose a template"
      body="Pick the one that sounds most like how you trade. You can change every condition later."
      footer="Templates describe market states, not trade signals."
      action={
        <Button variant="primary" size="lg" block disabled={!chosen} onClick={onContinue}>
          {chosen ? `Continue with ${chosen.name}` : 'Choose a template'}
        </Button>
      }
    >
      {templates.error ? (
        <ErrorState error={templates.error} onRetry={() => void templates.refetch()} />
      ) : templates.isLoading ? (
        <LoadingArea />
      ) : (
        <div className={s.choices} role="radiogroup" aria-label="Strategy template">
          {usable.map((t) => {
            const selected = t.id === chosen?.id;
            return (
              <button
                key={t.id}
                type="button"
                role="radio"
                aria-checked={selected}
                className={cx(s.choice, selected && s.choiceOn)}
                onClick={() => onChoose(t)}
              >
                <span className={s.choiceHead}>
                  <span className={s.choiceTitle}>{t.name}</span>
                  <DirectionPill direction={t.direction} size="xs" />
                  <SelectMark selected={selected} />
                </span>
                {t.description && <span className={s.choiceDesc}>{t.description}</span>}
                <span className={s.caption}>{conditionsCaption(t.rules)}</span>
                {selected && (
                  <span className={s.choiceRules}>
                    <ConditionList conditions={t.rules} presets={presets} showState={false} dense />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </StepFrame>
  );
}

function SelectMark({ selected }: { selected: boolean }) {
  return <span className={cx(s.mark, selected && s.markOn)}>{selected && <Icon name="check" size={14} />}</span>;
}

// --- S30: pick instruments ------------------------------------------------------------------------

function InstrumentsStep({
  strategyName,
  selected,
  onChange,
  preselect,
  error,
  creating,
  onCreate,
}: {
  strategyName: string;
  selected: string[];
  onChange: (ids: string[]) => void;
  preselect: (ids: string[]) => void;
  error: unknown;
  creating: boolean;
  onCreate: () => void;
}) {
  const instruments = useInstruments();
  // Only what the plan and country allow — no locked rows, this flow has no upsell in it. Instruments
  // a country excludes are absent from the payload, so a crypto-only account has no forex group.
  const usable = useMemo(() => (instruments.data ?? []).filter((i) => i.available !== false), [instruments.data]);
  useEffect(() => {
    // A sensible default, so "Get started, Continue, Create" works without a click on this step.
    if (usable.length) preselect(defaultPicks(usable));
  }, [usable, preselect]);

  // Over a hundred instruments: a search and the asset-class chips the main picker uses, so the
  // step is a choice rather than a scroll.
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');

  // Groups in first-seen order, metals by symbol as the picker files them.
  const allGroups = useMemo(() => [...new Set(usable.map(assetGroupLabel))], [usable]);
  // A search reorders strongest-first (exact symbol, then prefix), so the group holding "AAPL" leads
  // when the user types it; without one, the catalogue's own order stands.
  const groups = useMemo(() => {
    const out = new Map<string, InstrumentOut[]>();
    for (const i of query.trim() ? searchInstruments(usable, query) : usable) {
      const g = assetGroupLabel(i);
      if (filter !== 'all' && g !== filter) continue;
      out.set(g, [...(out.get(g) ?? []), i]);
    }
    return [...out.entries()];
  }, [usable, query, filter]);

  const sel = new Set(selected);
  const toggle = (id: string) => onChange(sel.has(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const count = selected.length;

  return (
    <StepFrame
      title="What should we watch?"
      body={`Pick as many as you like. ${strategyName} will run on each of them.`}
      footer="You can add more instruments any time from Watchlists."
      action={
        <>
          {error != null && (
            <Banner tone="neg">
              <strong>Couldn't finish setup.</strong> {messageOf(error)}
            </Banner>
          )}
          <Button variant="primary" size="lg" block disabled={count === 0} loading={creating} onClick={onCreate}>
            {creating
              ? 'Creating…'
              : count === 0
                ? 'Pick at least one instrument'
                : `Create watchlist · ${count} ${count === 1 ? 'instrument' : 'instruments'}`}
          </Button>
        </>
      }
    >
      {instruments.error ? (
        <ErrorState error={instruments.error} onRetry={() => void instruments.refetch()} />
      ) : instruments.isLoading ? (
        <LoadingArea />
      ) : (
        <>
          <div className={s.filters}>
            <SearchField value={query} onChange={setQuery} placeholder="Search instruments" className={s.search} />
            {allGroups.length > 1 && (
              <Segmented
                variant="chips"
                ariaLabel="Asset class"
                value={filter}
                onChange={setFilter}
                options={[{ value: 'all', label: 'All' }, ...allGroups.map((g) => ({ value: g, label: g }))]}
              />
            )}
          </div>
          {groups.length === 0 && <EmptyState icon="search" title="No instruments match" />}
          {groups.map(([group, items]) => (
          <section key={group} className={s.group} aria-label={group}>
            <h2 className={s.groupLabel}>{group}</h2>
            <div className={s.grid}>
              {items.map((i) => {
                const on = sel.has(i.id);
                return (
                  <button
                    key={i.id}
                    type="button"
                    aria-pressed={on}
                    className={cx(s.pick, on && s.choiceOn)}
                    onClick={() => toggle(i.id)}
                  >
                    <span className={s.pickText}>
                      <span className={s.pickSymbol}>{i.symbol}</span>
                      <span className={s.pickName}>{i.name}</span>
                    </span>
                    <SelectMark selected={on} />
                  </button>
                );
              })}
            </div>
          </section>
          ))}
        </>
      )}
    </StepFrame>
  );
}

// --- S31: done ------------------------------------------------------------------------------------

function DoneStep({
  result,
  conditions,
  instrumentIds,
  doneLabel,
  onDone,
  onInviteCode,
}: {
  result: OnboardingSetupOut;
  conditions: ConditionRowData[] | undefined;
  instrumentIds: string[];
  doneLabel: string;
  onDone: () => void;
  onInviteCode: () => void;
}) {
  const presets = useWeightPresets().data;
  const instruments = useInstruments().data ?? [];
  // `WatchlistOut` carries a count, not its members, so the symbols come from the choice just made.
  // A setup an earlier attempt already recorded (`already_completed`) is described by the response
  // alone, without the chips.
  const symbols = result.already_completed
    ? []
    : instrumentIds.map((id) => instruments.find((i) => i.id === id)?.symbol).filter((x): x is string => !!x);
  const strategy = result.strategy;
  const watchlist = result.watchlist;
  return (
    <StepFrame
      centred
      title="You're all set"
      body="SixOne8 checks each instrument as every bar closes and alerts you when the conditions line up."
      action={
        <Button variant="primary" size="lg" block onClick={onDone}>
          {doneLabel}
        </Button>
      }
    >
      <span className={s.doneMark}>
        <Icon name="check" size={28} />
      </span>
      {strategy && (
        <div className={s.doneCard}>
          <span className={s.doneLabelRow}>
            <span className={s.groupLabel}>Strategy</span>
            {strategy.enabled && (
              <Pill tone="pos" size="xs">
                Active
              </Pill>
            )}
          </span>
          <span className={s.choiceHead}>
            <span className={s.choiceTitle}>{strategy.name}</span>
            <DirectionPill direction={strategy.direction} size="xs" />
          </span>
          {/* The copy re-points at the template's (or share's) rules, so its conditions are the strategy's. */}
          {conditions && !result.already_completed && (
            <ConditionList conditions={conditions} presets={presets} showState={false} dense />
          )}
        </div>
      )}
      {watchlist && (
        <div className={s.doneCard}>
          <span className={s.groupLabel}>Watchlist</span>
          <span className={s.choiceTitle}>{watchlist.name}</span>
          {symbols.length > 0 && (
            <span className={s.symbols}>
              {symbols.map((sym) => (
                <Pill key={sym} size="xs">
                  {sym}
                </Pill>
              ))}
            </span>
          )}
        </div>
      )}
      <Banner tone="brand">Both can be edited or deleted at any time from Strategies and Watchlists.</Banner>
      {/* A link, not a field: the first run stays frictionless, and someone holding an invite code
          finds the door here, before they subscribe. The code box is on Account. */}
      <button type="button" className={s.link} onClick={onInviteCode}>
        Have an invite code?
      </button>
    </StepFrame>
  );
}
